import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { BASE_AUTH_URL, ROLE } from '../components/constants';
import { fetchAllUsers, updateUserRole, deactivateUser, activateUser } from '../slice/usersSlice';
import { fetchAllOrganizations, updateUserOrganization } from '../slice/organizationsSlice';
import { fetchUserData } from '../slice/userDataSlice';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useThemeColor } from '../hooks/useThemeColor';
import { useTranslation } from '../i18n/LanguageContext';
import { BASE_URL } from '../components/constants';
import { getErrorMessage } from '../utils/errorMessage';
import { Search, MoreHorizontal, UserRoundPlus, Building2, ChevronDown, Check, Loader2, X, Filter as FilterIcon, Activity, PowerOff, Power, Send, KeyRound } from 'lucide-react';
import { authHeader, authHeaders } from '../utils/authHeaders';
import MultiSelectDropdown from '../components/sub-components/MultiSelectDropdown';

const ROLE_FILTER_OPTIONS = [
  { code: ROLE.SUPER_ADMIN, tKey: 'users.roleSuperAdmin' },
  { code: ROLE.ADMIN,       tKey: 'users.roleAdmin' },
  { code: ROLE.NO_LICENSE,  tKey: 'users.roleNoLicense' },
  { code: ROLE.NORMAL,      tKey: 'users.roleNormal' },
];

const SORT_OPTIONS = [
  { code: 'newest',     tKey: 'users.filterSortNewest' },
  { code: 'oldest',     tKey: 'users.filterSortOldest' },
  { code: 'name_asc',   tKey: 'users.filterSortNameAsc' },
  { code: 'name_desc',  tKey: 'users.filterSortNameDesc' },
];

// Multi-select dropdown with chips for selected items.

const FilterModal = ({
  isOpen,
  onClose,
  onApply,
  onClearAll,
  initialUserGroups,
  initialOrganizations,
  initialRoles,
  initialSortBy,
  userGroupOptions,
  organizationOptions,
  aispaceColor,
  callerIsSuperAdmin,
}) => {
  const { t } = useTranslation();
  const [userGroups, setUserGroups] = useState(initialUserGroups || []);
  const [organizationsSel, setOrganizationsSel] = useState(initialOrganizations || []);
  const [roles, setRoles] = useState(initialRoles || []);
  const [sortBy, setSortBy] = useState(initialSortBy || 'newest');
  const [sortOpen, setSortOpen] = useState(false);
  const [orgUserGroupOptions, setOrgUserGroupOptions] = useState([]);
  const sortRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setUserGroups(initialUserGroups || []);
      setOrganizationsSel(initialOrganizations || []);
      setRoles(initialRoles || []);
      setSortBy(initialSortBy || 'newest');
      setSortOpen(false);
    }
  }, [isOpen, initialUserGroups, initialOrganizations, initialRoles, initialSortBy]);

  useEffect(() => {
    const onDoc = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // When organisations are selected, load user-groups (departments) belonging to
  // those orgs. When none are selected, fall back to the parent-provided list.
  useEffect(() => {
    if (organizationsSel.length === 0) {
      setOrgUserGroupOptions([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      organizationsSel.map((orgId) =>
        axios
          .get(`${BASE_URL}/departments/${orgId}`, authHeaders())
          .then((res) => res.data?.departments || [])
          .catch(() => []),
      ),
    ).then((perOrg) => {
      if (cancelled) return;
      const seen = new Set();
      const opts = [];
      perOrg.flat().forEach((d) => {
        const name = d.department_name;
        if (name && !seen.has(name)) {
          seen.add(name);
          opts.push({ code: name, label: name });
        }
      });
      setOrgUserGroupOptions(opts);
    });
    return () => { cancelled = true; };
  }, [organizationsSel]);

  // Active options: org-dependent when any org is selected, otherwise the
  // parent-provided list (all known user-groups across users).
  const activeUserGroupOptions = organizationsSel.length > 0 ? orgUserGroupOptions : userGroupOptions;

  // Drop previously selected user groups that are no longer in the active options.
  useEffect(() => {
    const valid = new Set(activeUserGroupOptions.map((o) => o.code));
    setUserGroups((prev) => prev.filter((c) => valid.has(c)));
  }, [activeUserGroupOptions]);

  if (!isOpen) return null;

  const toggleRole = (code) => {
    setRoles((prev) => (prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]));
  };

  const handleClearAll = () => {
    setUserGroups([]);
    setOrganizationsSel([]);
    setRoles([]);
    setSortBy('newest');
    onClearAll();
  };

  const handleApply = () => {
    onApply({ userGroups, organizations: organizationsSel, roles, sortBy });
    onClose();
  };

  const sortLabel = SORT_OPTIONS.find((o) => o.code === sortBy);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120] p-4">
      <div className="bg-white rounded-2xl w-full max-w-[560px] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
          <h2 className="text-[20px] font-bold text-[#111827]">{t('users.filterModalTitle')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Organisation (selected first — drives the User Group options below) */}
          <div>
            <label className="block text-[15px] font-semibold text-[#111827] mb-2">
              {t('users.filterOrganizationLabel')}
            </label>
            <MultiSelectDropdown
              options={organizationOptions}
              selectedCodes={organizationsSel}
              onChange={setOrganizationsSel}
              placeholder={t('users.filterOrganizationPlaceholder')}
              aispaceColor={aispaceColor}
            />
          </div>

          {/* User Group — depends on the selected organisations above */}
          <div>
            <label className="block text-[15px] font-semibold text-[#111827] mb-2">
              {t('users.filterUserGroupLabel')}
            </label>
            <MultiSelectDropdown
              options={activeUserGroupOptions}
              selectedCodes={userGroups}
              onChange={setUserGroups}
              placeholder={t('users.filterUserGroupPlaceholder')}
              aispaceColor={aispaceColor}
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-[15px] font-semibold text-[#111827] mb-3">
              {t('users.filterRoleLabel')}
            </label>
            <div className="grid grid-cols-2 gap-y-3 gap-x-4">
              {ROLE_FILTER_OPTIONS.filter(opt => callerIsSuperAdmin || opt.code !== ROLE.SUPER_ADMIN).map((opt) => {
                const checked = roles.includes(opt.code);
                return (
                  <label
                    key={opt.code}
                    className="flex items-center gap-3 cursor-pointer select-none"
                  >
                    <span
                      onClick={() => toggleRole(opt.code)}
                      className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                      style={
                        checked
                          ? { borderColor: aispaceColor, backgroundColor: aispaceColor }
                          : { borderColor: '#D1D5DB', backgroundColor: 'white' }
                      }
                    >
                      {checked && <Check size={14} className="text-white" strokeWidth={3} />}
                    </span>
                    <span
                      className="text-[15px] text-[#111827]"
                      onClick={() => toggleRole(opt.code)}
                    >
                      {t(opt.tKey)}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-[15px] font-semibold text-[#111827] mb-2">
              {t('users.filterSortByLabel')}
            </label>
            <div className="relative" ref={sortRef}>
              <button
                type="button"
                onClick={() => setSortOpen((o) => !o)}
                className="w-full flex items-center justify-between border rounded-lg px-4 py-3 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors focus:outline-none"
                style={sortOpen ? { borderColor: aispaceColor } : { borderColor: '#E5E7EB' }}
              >
                <span className="text-[#111827]">{sortLabel ? t(sortLabel.tKey) : ''}</span>
                <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
              </button>
              {sortOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 overflow-hidden">
                  {SORT_OPTIONS.map((opt) => {
                    const isSel = sortBy === opt.code;
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => { setSortBy(opt.code); setSortOpen(false); }}
                        className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${isSel ? 'bg-[#F3F4F6]' : ''}`}
                      >
                        <span>{t(opt.tKey)}</span>
                        {isSel && <Check size={16} style={{ color: aispaceColor }} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          <button
            onClick={handleClearAll}
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            {t('users.filterClearAll')}
          </button>
          <button
            onClick={handleApply}
            className="px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors min-w-[100px]"
            style={{ backgroundColor: aispaceColor }}
          >
            {t('users.filterApply')}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrganizationModal = ({ isOpen, onClose, onConfirm, organizations = [], currentOrganization = null, currentDepartmentIds = [], aispaceColor, isAdminCaller = false, hideOrgPicker = false }) => {
  const { t } = useTranslation();
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [orgDepartments, setOrgDepartments] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const orgDropdownRef = useRef(null);

  // Pre-fill on open: keep the user's existing org + department assignments
  // selected so the admin can edit rather than re-enter from scratch.
  useEffect(() => {
    if (isOpen) {
      setSelectedOrganization(currentOrganization || '');
      setSelectedDepartmentIds((currentDepartmentIds || []).map(String));
      setOrgDropdownOpen(false);
      setIsSaving(false);
    }
  }, [isOpen, currentOrganization, currentDepartmentIds]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target)) setOrgDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Fetch departments whenever the selected organization changes.
  // Selection (selectedDepartmentIds) is cleared at the org-change call site
  // when the user explicitly picks a different org — not here — so the initial
  // pre-filled selection is preserved on first open.
  useEffect(() => {
    if (!selectedOrganization) {
      setOrgDepartments([]);
      return;
    }
    let cancelled = false;
    setDepartmentsLoading(true);
    axios
      .get(`${BASE_URL}/departments/${selectedOrganization}`, authHeaders())
      .then((res) => {
        if (cancelled) return;
        setOrgDepartments(res.data?.departments || []);
      })
      .catch(() => {
        if (cancelled) return;
        setOrgDepartments([]);
      })
      .finally(() => {
        if (!cancelled) setDepartmentsLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedOrganization]);

  if (!isOpen) return null;

  const selectedOrgName = selectedOrganization
    ? (organizations.find(o => o.organization_id === selectedOrganization)?.organization_name || selectedOrganization)
    : t('users.orgModalNoOrganization');

  const departmentOptions = orgDepartments.map((d) => ({
    code: String(d.id),
    label: d.department_name,
  }));

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
      <div className="bg-white rounded-xl w-[420px] shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] rounded-t-xl">
          <h2 className="text-[16px] font-semibold text-[#111827]">{t('users.orgModalTitle')}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Only super-admin can change the user's organization. */}
          {!isAdminCaller && !hideOrgPicker && (
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-2">
              {t('users.orgModalSelectLabel')}
            </label>
            <div className="relative" ref={orgDropdownRef}>
              <button
                type="button"
                onClick={() => setOrgDropdownOpen(v => !v)}
                className="w-full flex items-center justify-between border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors focus:outline-none"
                style={orgDropdownOpen ? { borderColor: aispaceColor } : {}}
              >
                <span className={selectedOrganization ? 'text-[#111827]' : 'text-[#9CA3AF]'}>{selectedOrgName}</span>
                <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {orgDropdownOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 max-h-[260px] overflow-y-auto py-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedOrganization !== '') setSelectedDepartmentIds([]);
                      setSelectedOrganization('');
                      setOrgDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${!selectedOrganization ? 'bg-[#F3F4F6]' : ''}`}
                  >
                    <span>{t('users.orgModalNoOrganization')}</span>
                    {!selectedOrganization && <Check size={16} style={{ color: aispaceColor }} />}
                  </button>
                  {organizations.map((org) => {
                    const isSelected = org.organization_id === selectedOrganization;
                    return (
                      <button
                        key={org.organization_id}
                        type="button"
                        onClick={() => {
                          if (org.organization_id !== selectedOrganization) setSelectedDepartmentIds([]);
                          setSelectedOrganization(org.organization_id);
                          setOrgDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${isSelected ? 'bg-[#F3F4F6]' : ''}`}
                      >
                        <span className="truncate">{org.organization_name}</span>
                        {isSelected && <Check size={16} style={{ color: aispaceColor }} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Assign User Group (Benutzergruppe) */}
          <div className="mt-5">
            <label className="block text-[14px] font-medium text-[#111827] mb-2">
              {t('users.orgModalAssignUserGroupLabel')}
            </label>
            {isAdminCaller ? (
              /* Target user is admin/super-admin: read-only — show all org departments */
              <div className="min-h-[46px] flex flex-wrap gap-2 border border-[#E5E7EB] rounded-lg px-3 py-2 bg-[#F9FAFB]">
                {departmentsLoading ? (
                  <span className="text-[13px] text-[#9CA3AF]">Benutzergruppen werden geladen...</span>
                ) : departmentOptions.length === 0 ? (
                  <span className="text-[13px] text-[#9CA3AF]">{t('users.orgModalAssignUserGroupPlaceholder')}</span>
                ) : (
                  departmentOptions.map((dept) => (
                    <span key={dept.code} className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium bg-white border border-[#E5E7EB] text-[#374151]">
                      {dept.label}
                    </span>
                  ))
                )}
              </div>
            ) : (
              /* Super-Admin: editable multi-select */
              <>
                <div className={!selectedOrganization || departmentsLoading ? 'opacity-60 pointer-events-none' : ''}>
                  <MultiSelectDropdown
                    options={departmentOptions}
                    selectedCodes={selectedDepartmentIds}
                    onChange={setSelectedDepartmentIds}
                    placeholder={t('users.orgModalAssignUserGroupPlaceholder')}
                    aispaceColor={aispaceColor}
                  />
                </div>
                {departmentsLoading && (
                  <p className="mt-1.5 text-[12px] text-[#6B7280]">Benutzergruppen werden geladen...</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-xl">
          <button
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
            onClick={onClose}
            disabled={isSaving}
          >
            {isAdminCaller ? t('common.close') : t('common.cancel')}
          </button>
          {!isAdminCaller && (
          <button
            className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
            style={{ backgroundColor: aispaceColor }}
            onClick={async () => {
              if (isSaving) return;
              setIsSaving(true);
              try {
                await onConfirm(
                  selectedOrganization || null,
                  selectedDepartmentIds,
                );
                onClose();
              } finally {
                setIsSaving(false);
              }
            }}
            disabled={isSaving || !selectedOrganization}
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>{t('users.orgModalSaving')}</span>
              </>
            ) : (
              <span>{t('common.save')}</span>
            )}
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

const RoleModal = ({ isOpen, onClose, onConfirm, currentRole, userName, aispaceColor, callerIsSuperAdmin }) => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const dropdownRef = useRef(null);
  // ITEM 9: ADMIN cannot assign SUPER_ADMIN role
  const roleOptions = callerIsSuperAdmin
    ? [ROLE.NO_LICENSE, ROLE.NORMAL, ROLE.ADMIN, ROLE.SUPER_ADMIN]
    : [ROLE.NO_LICENSE, ROLE.NORMAL, ROLE.ADMIN];

  useEffect(() => {
    setSelectedRole('');
    setDropdownOpen(false);
    setIsSaving(false);
  }, [currentRole, isOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (!isOpen) return null;

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case ROLE.SUPER_ADMIN:
        return 'bg-[#FEE2E2] text-[#DC2626]';
      case ROLE.ADMIN:
        return 'bg-[#EDE9FE] text-[#7C3AED]';
      case ROLE.NORMAL:
        return 'bg-[#DBEAFE] text-[#2563EB]';
      default:
        return 'bg-[#F3F4F6] text-[#6B7280]';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case ROLE.SUPER_ADMIN:
        return t('users.roleSuperAdmin');
      case ROLE.ADMIN:
        return t('users.roleAdmin');
      case ROLE.NORMAL:
        return t('users.roleNormal');
      default:
        return t('users.roleNoLicense');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
      <div className="bg-white rounded-xl w-[420px] shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] rounded-t-xl">
          <h2 className="text-[16px] font-semibold text-[#111827]">{t('users.roleModalTitle')}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Name */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-[14px] text-[#6B7280]">{t('users.roleModalNameLabel')}</span>
            <span className="text-[14px] font-medium text-[#111827]">{userName}</span>
          </div>

          {/* Current Role */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-[14px] text-[#6B7280]">{t('users.roleModalCurrentRoleLabel')}</span>
            <span className={`px-3 py-1 text-[12px] font-medium rounded-full ${getRoleBadgeStyle(currentRole)}`}>
              {getRoleLabel(currentRole)}
            </span>
          </div>

          {/* Select New Role - custom dropdown */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-2">
              {t('users.roleModalSelectLabel')}
            </label>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen(v => !v)}
                className="w-full flex items-center justify-between border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors focus:outline-none"
                style={dropdownOpen ? { borderColor: aispaceColor } : {}}
              >
                <span className={selectedRole ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                  {selectedRole ? getRoleLabel(selectedRole) : t('users.roleModalSelectPlaceholder')}
                </span>
                <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 max-h-[260px] overflow-y-auto py-1">
                  {roleOptions.filter(role => role !== currentRole).length === 0 ? (
                    <div className="px-4 py-3 text-[13px] text-[#9CA3AF]">{t('users.roleModalNoOtherRoles')}</div>
                  ) : (
                    roleOptions.filter(role => role !== currentRole).map((role) => {
                      const isSelected = role === selectedRole;
                      return (
                        <button
                          key={role}
                          type="button"
                          onClick={() => { setSelectedRole(role); setDropdownOpen(false); }}
                          className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${isSelected ? 'bg-[#F3F4F6]' : ''}`}
                        >
                          <span>{getRoleLabel(role)}</span>
                          {isSelected && <Check size={16} style={{ color: aispaceColor }} />}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-xl">
          <button
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          <button
            className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
            style={{ backgroundColor: aispaceColor }}
            onClick={async () => {
              if (!selectedRole || isSaving) return;
              setIsSaving(true);
              try {
                await onConfirm(selectedRole);
                onClose();
              } finally {
                setIsSaving(false);
              }
            }}
            disabled={!selectedRole || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>{t('users.roleModalSaving')}</span>
              </>
            ) : (
              <span>{t('common.save')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ActivityModal = ({ isOpen, onClose, userEmail, userName, aispaceColor }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !userEmail) return;
    setLoading(true);
    setError('');
    axios.get(`${BASE_URL}/login-audit`, {
      params: { email: userEmail },
      headers: { ...authHeader() },
    })
      .then((res) => setLogs(res.data.logs || []))
      .catch(() => setError(t('users.activityError')))
      .finally(() => setLoading(false));
  }, [isOpen, userEmail, t]);

  if (!isOpen) return null;

  const methodLabels = {
    password: t('users.activityMethodPassword'),
    otp: t('users.activityMethodOtp'),
    microsoft: t('users.activityMethodMicrosoft'),
    mfa: t('users.activityMethodMfa'),
    mfa_setup: t('users.activityMethodMfaSetup'),
    mfa_recovery: t('users.activityMethodMfaRecovery'),
    mfa_reset: t('users.activityMethodMfaReset'),
    otp_request: t('users.activityMethodOtpRequest'),
    reset_request: t('users.activityMethodResetRequest'),
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
      <div className="bg-white rounded-xl w-[640px] max-h-[80vh] shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] rounded-t-xl flex-shrink-0">
          <div className="min-w-0 pr-4">
            <h2 className="text-[16px] font-semibold text-[#111827]">{t('users.activityModalTitle')}</h2>
            <p className="text-[13px] text-[#6B7280] mt-0.5 truncate">{userName} · {userEmail}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={22} className="animate-spin" style={{ color: aispaceColor }} />
            </div>
          ) : error ? (
            <div className="px-6 py-10 text-center text-[14px] text-red-600">{error}</div>
          ) : logs.length === 0 ? (
            <div className="px-6 py-10 text-center text-[14px] text-[#9CA3AF]">{t('users.activityEmpty')}</div>
          ) : (
            <table className="w-full text-left border-collapse m-0">
              <thead>
                <tr className="bg-[#F9FAFB] border-t border-b border-[#E5E7EB]">
                  <th className="px-6 py-3 text-[13px] font-medium text-[#6B7280] whitespace-nowrap text-center border-0 bg-transparent">{t('users.activityColMethod')}</th>
                  <th className="px-3 py-3 text-[13px] font-medium text-[#6B7280] whitespace-nowrap text-center border-0 bg-transparent">{t('users.activityColStatus')}</th>
                  <th className="px-3 py-3 text-[13px] font-medium text-[#6B7280] whitespace-nowrap text-center border-0 bg-transparent">{t('users.activityColIp')}</th>
                  <th className="px-3 py-3 pr-6 text-[13px] font-medium text-[#6B7280] whitespace-nowrap text-center border-0 bg-transparent">{t('users.activityColTime')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-6 py-3 text-[13px] text-[#111827] whitespace-nowrap text-center border-0">
                      {methodLabels[log.login_method] || log.login_method}
                      {log.performed_by && (
                        <div className="text-[11px] text-[#9CA3AF] mt-1">{t('users.activityPerformedBy', { email: log.performed_by })}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center border-0">
                      <span
                        className={`inline-block px-2.5 py-1 text-[12px] font-medium rounded-full whitespace-nowrap ${
                          log.status === 'success' ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#FEF2F2] text-[#DC2626]'
                        }`}
                      >
                        {log.status === 'success' ? t('users.activityStatusSuccess') : t('users.activityStatusFailed')}
                      </span>
                      {log.status !== 'success' && log.failure_reason && (
                        <div className="text-[11px] text-[#9CA3AF] mt-1">{log.failure_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-[13px] text-[#6B7280] font-mono whitespace-nowrap text-center border-0">
                      {log.ip_address || '-'}
                    </td>
                    <td className="px-3 py-3 pr-6 text-[13px] text-[#6B7280] whitespace-nowrap text-center border-0">
                      {log.created_at ? new Date(log.created_at).toLocaleString('de-DE') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-xl flex-shrink-0">
          <button
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusConfirmModal = ({ isOpen, onClose, onConfirm, action, userName, userEmail, loading, aispaceColor }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;
  const isResetMfa = action === 'resetMfa';
  const isDeactivate = action === 'deactivate' || isResetMfa;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120]">
      <div className="bg-white rounded-2xl w-[400px] shadow-xl px-6 py-8 text-center">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDeactivate ? 'bg-[#FEE2E2]' : 'bg-[#DCFCE7]'}`}>
          {isResetMfa ? <KeyRound size={26} className="text-[#DC2626]" /> : isDeactivate ? <PowerOff size={26} className="text-[#DC2626]" /> : <Power size={26} className="text-[#16A34A]" />}
        </div>
        <h3 className="text-[17px] font-semibold text-[#111827] mb-1.5">
          {isResetMfa ? t('users.resetMfaConfirmTitle') : isDeactivate ? t('users.deactivateConfirmTitle') : t('users.activateConfirmTitle')}
        </h3>
        <p className="text-[14px] text-[#6B7280] leading-relaxed mb-6">
          {t(isResetMfa ? 'users.resetMfaConfirmMessage' : isDeactivate ? 'users.deactivateConfirmMessage' : 'users.activateConfirmMessage', {
            name: userName || userEmail,
          })}
        </p>
        <div className="flex items-center gap-3">
          <button
            className="flex-1 px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDeactivate ? 'bg-[#DC2626]' : ''}`}
            style={isDeactivate ? undefined : { backgroundColor: aispaceColor }}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              isResetMfa ? t('users.actionResetMfa') : isDeactivate ? t('users.actionDeactivateUser') : t('users.actionActivateUser')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddUserModal = ({ isOpen, onClose, aispaceColor, callerIsSuperAdmin, callerHasOrg = true, organizations = [], callerOrgId = null, onSuccess, existingEmails = [] }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [fullname, setFullname] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('');
  const [roleOpen, setRoleOpen] = useState(false);
  const [organizationId, setOrganizationId] = useState(callerOrgId || '');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');
  const roleRef = useRef(null);
  const orgRef = useRef(null);

  const roleOptions = callerIsSuperAdmin
    ? [ROLE.NO_LICENSE, ROLE.NORMAL, ROLE.ADMIN, ROLE.SUPER_ADMIN]
    : [ROLE.NO_LICENSE, ROLE.NORMAL, ROLE.ADMIN];

  const roleLabels = {
    [ROLE.NO_LICENSE]: t('users.roleNoLicense'),
    [ROLE.NORMAL]: t('users.roleNormal'),
    [ROLE.ADMIN]: t('users.roleAdmin'),
    [ROLE.SUPER_ADMIN]: t('users.roleSuperAdmin'),
  };

  useEffect(() => {
    setEmail(''); setFullname(''); setPassword(''); setRole('');
    setEmailError(''); setLoading(false);
    setShowPassword(false); setRoleOpen(false);
    setError('');
    setOrganizationId(callerOrgId || '');
    setOrgDropdownOpen(false);
  }, [isOpen, callerOrgId]);

  useEffect(() => {
    const handler = (e) => {
      if (roleRef.current && !roleRef.current.contains(e.target)) setRoleOpen(false);
      if (orgRef.current && !orgRef.current.contains(e.target)) setOrgDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const checkEmailExists = (val) => {
    setEmailError('');
    if (!EMAIL_REGEX.test(val.trim())) return;
    if (existingEmails.includes(val.trim().toLowerCase())) {
      setEmailError(t('users.inviteEmailAlreadyExists'));
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setError('');
    if (!email.trim() || !fullname.trim() || !password || !role) {
      setError(t('users.addUserFieldRequired'));
      return;
    }
    if (callerIsSuperAdmin && !organizationId) {
      setError(t('users.addUserSelectOrgRequired'));
      return;
    }
    if (!callerIsSuperAdmin && !callerHasOrg) {
      setError(t('users.addUserNoOrgError'));
      return;
    }
    if (emailError) return;
    setLoading(true);
    try {
      const payload = { email: email.trim(), fullname: fullname.trim(), password, role, organization_id: organizationId || null };
      await axios.post(
        `${BASE_AUTH_URL}/admin/add-user`,
        payload,
        authHeaders(),
      );
      onSuccess();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, t('users.addUserErrorToast')));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full border rounded-lg px-4 py-3 text-[14px] outline-none transition-colors';

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120] p-4">
      <div className="bg-white rounded-2xl w-full max-w-[480px] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
          <div>
            <h2 className="text-[20px] font-bold text-[#111827]">{t('users.addUserModalTitle')}</h2>
            <p className="text-[13px] text-[#6B7280] mt-0.5">{t('users.addUserModalSubtitle')}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</div>
          )}

          {/* Email */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-1.5">{t('users.inviteModalEmailLabel')}</label>
            <input
              type="email" value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
              onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; checkEmailExists(e.target.value); }}
              onFocus={(e) => (e.target.style.borderColor = emailError ? '#EF4444' : aispaceColor)}
              placeholder="user@example.com"
              className={inputCls}
              style={{ borderColor: emailError ? '#EF4444' : '#E5E7EB' }}
            />
            {emailError && <p className="mt-1.5 text-[12px] text-red-600 flex items-center gap-1"><X size={12} />{emailError}</p>}
          </div>

          {/* Fullname */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-1.5">{t('users.addUserFullnameLabel')}</label>
            <input
              type="text" value={fullname} onChange={(e) => setFullname(e.target.value)}
              onFocus={(e) => (e.target.style.borderColor = aispaceColor)}
              onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
              placeholder={t('users.addUserFullnameLabel')}
              className={inputCls} style={{ borderColor: '#E5E7EB' }}
            />
          </div>

          {/* Organization */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-1.5">{t('users.addUserOrganizationLabel')}</label>
            {callerIsSuperAdmin ? (
              <div className="relative" ref={orgRef}>
                <button type="button" onClick={() => setOrgDropdownOpen((o) => !o)}
                  className="w-full flex items-center justify-between border rounded-lg px-4 py-3 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors outline-none"
                  style={orgDropdownOpen ? { borderColor: aispaceColor } : { borderColor: '#E5E7EB' }}>
                  <span className={organizationId ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                    {organizationId
                      ? (organizations.find((o) => o.organization_id === organizationId)?.organization_name || organizationId)
                      : t('users.addUserSelectOrgPlaceholder')}
                  </span>
                  <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {orgDropdownOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 max-h-[220px] overflow-y-auto py-1">
                    {organizations.map((org) => (
                      <button key={org.organization_id} type="button"
                        onClick={() => { setOrganizationId(org.organization_id); setOrgDropdownOpen(false); }}
                        className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${organizationId === org.organization_id ? 'bg-[#F3F4F6]' : ''}`}>
                        <span className="truncate">{org.organization_name}</span>
                        {organizationId === org.organization_id && <Check size={16} style={{ color: aispaceColor }} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full border rounded-lg px-4 py-3 text-[14px] bg-[#F9FAFB] text-[#6B7280] border-[#E5E7EB] cursor-not-allowed">
                {callerHasOrg
                  ? (organizations.find((o) => o.organization_id === callerOrgId)?.organization_name || callerOrgId)
                  : t('users.addUserNoOrgError')}
              </div>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-1.5">{t('users.addUserPasswordLabel')}</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={(e) => (e.target.style.borderColor = aispaceColor)}
                onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                placeholder="••••••••"
                className={inputCls + ' pr-10'} style={{ borderColor: '#E5E7EB' }}
              />
              <button type="button" onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280] transition-colors">
                {showPassword
                  ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>
                  : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.6 18.6 0 0 1 4.22-5.06M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/></svg>
                }
              </button>
            </div>
          </div>

          {/* Role */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-1.5">{t('users.addUserRoleLabel')}</label>
            <div className="relative" ref={roleRef}>
              <button type="button" onClick={() => setRoleOpen((o) => !o)}
                className="w-full flex items-center justify-between border rounded-lg px-4 py-3 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors outline-none"
                style={roleOpen ? { borderColor: aispaceColor } : { borderColor: '#E5E7EB' }}>
                <span className={role ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                  {role ? roleLabels[role] : t('users.addUserRolePlaceholder')}
                </span>
                <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${roleOpen ? 'rotate-180' : ''}`} />
              </button>
              {roleOpen && (
                <div className="absolute left-0 right-0 bottom-full mb-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 py-1">
                  {roleOptions.map((opt) => (
                    <button key={opt} type="button" onClick={() => { setRole(opt); setRoleOpen(false); }}
                      className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${role === opt ? 'bg-[#F3F4F6]' : ''}`}>
                      <span>{roleLabels[opt]}</span>
                      {role === opt && <Check size={16} style={{ color: aispaceColor }} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-2xl">
          <button onClick={onClose}
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors">
            {t('common.cancel')}
          </button>
          <button onClick={handleSubmit} disabled={loading || !!emailError}
            className="flex items-center justify-center gap-2 px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]"
            style={{ backgroundColor: aispaceColor }}>
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /><span>{t('users.addUserSubmitting')}</span></>
            ) : (
              <span>{t('users.addUserSubmitButton')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const InviteUserModal = ({ isOpen, onClose, aispaceColor, callerIsSuperAdmin, callerHasOrg = true, organizations = [], callerOrgId = null, onSuccess, existingEmails = [] }) => {
  const { t, language } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [roleOpen, setRoleOpen] = useState(false);
  const [organizationId, setOrganizationId] = useState(callerOrgId || '');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');
  const roleRef = useRef(null);
  const orgRef = useRef(null);

  const roleOptions = callerIsSuperAdmin
    ? [ROLE.NO_LICENSE, ROLE.NORMAL, ROLE.ADMIN, ROLE.SUPER_ADMIN]
    : [ROLE.NO_LICENSE, ROLE.NORMAL, ROLE.ADMIN];

  const roleLabels = {
    [ROLE.NO_LICENSE]: t('users.roleNoLicense'),
    [ROLE.NORMAL]: t('users.roleNormal'),
    [ROLE.ADMIN]: t('users.roleAdmin'),
    [ROLE.SUPER_ADMIN]: t('users.roleSuperAdmin'),
  };

  useEffect(() => {
    if (isOpen) {
      setEmail(''); setRole(''); setError(''); setEmailError('');
      setLoading(false); setCheckingEmail(false); setRoleOpen(false);
      setOrganizationId(callerOrgId || ''); setOrgDropdownOpen(false);
    }
  }, [isOpen, callerOrgId]);

  const checkEmailExists = (val) => {
    setEmailError('');
    if (!EMAIL_REGEX.test(val.trim())) return;
    setCheckingEmail(true);
    setTimeout(() => {
      const exists = existingEmails.includes(val.trim().toLowerCase());
      if (exists) setEmailError(t('users.inviteEmailAlreadyExists'));
      setCheckingEmail(false);
    }, 0);
  };

  useEffect(() => {
    const handler = (e) => {
      if (roleRef.current && !roleRef.current.contains(e.target)) setRoleOpen(false);
      if (orgRef.current && !orgRef.current.contains(e.target)) setOrgDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!isOpen) return null;

  const handleSend = async () => {
    setError('');
    if (!email.trim() || !role) {
      setError(t('users.inviteModalFieldRequired'));
      return;
    }
    if (callerIsSuperAdmin && !organizationId) {
      setError(t('users.addUserSelectOrgRequired'));
      return;
    }
    if (!callerIsSuperAdmin && !callerHasOrg) {
      setError(t('users.addUserNoOrgError'));
      return;
    }
    if (emailError) return;
    setLoading(true);
    try {
      await axios.post(
        `${BASE_AUTH_URL}/invite-user`,
        { email: email.trim(), role, language, organization_id: organizationId || null },
        authHeaders(),
      );
      onSuccess();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, t('users.inviteModalErrorToast')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120] p-4">
      <div className="bg-white rounded-2xl w-full max-w-[480px] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
          <div>
            <h2 className="text-[20px] font-bold text-[#111827]">{t('users.inviteModalTitle')}</h2>
            <p className="text-[13px] text-[#6B7280] mt-0.5">{t('users.inviteModalSubtitle')}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-1.5">
              {t('users.inviteModalEmailLabel')}
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                onBlur={(e) => {
                  e.target.style.borderColor = emailError ? '#EF4444' : '#E5E7EB';
                  checkEmailExists(e.target.value);
                }}
                onFocus={(e) => (e.target.style.borderColor = emailError ? '#EF4444' : aispaceColor)}
                placeholder="user@example.com"
                className="w-full border rounded-lg px-4 py-3 text-[14px] outline-none transition-colors"
                style={{ borderColor: emailError ? '#EF4444' : '#E5E7EB' }}
              />
              {checkingEmail && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 size={16} className="animate-spin text-[#9CA3AF]" />
                </div>
              )}
            </div>
            {emailError && (
              <p className="mt-1.5 text-[12px] text-red-600 flex items-center gap-1">
                <X size={12} />
                {emailError}
              </p>
            )}
          </div>

          {/* Organization */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-1.5">
              {t('users.addUserOrganizationLabel')}
            </label>
            {callerIsSuperAdmin ? (
              <div className="relative" ref={orgRef}>
                <button type="button" onClick={() => setOrgDropdownOpen((o) => !o)}
                  className="w-full flex items-center justify-between border rounded-lg px-4 py-3 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors outline-none"
                  style={orgDropdownOpen ? { borderColor: aispaceColor } : { borderColor: '#E5E7EB' }}>
                  <span className={organizationId ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                    {organizationId
                      ? (organizations.find((o) => o.organization_id === organizationId)?.organization_name || organizationId)
                      : t('users.addUserSelectOrgPlaceholder')}
                  </span>
                  <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {orgDropdownOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 max-h-[220px] overflow-y-auto py-1">
                    {organizations.map((org) => (
                      <button key={org.organization_id} type="button"
                        onClick={() => { setOrganizationId(org.organization_id); setOrgDropdownOpen(false); }}
                        className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${organizationId === org.organization_id ? 'bg-[#F3F4F6]' : ''}`}>
                        <span className="truncate">{org.organization_name}</span>
                        {organizationId === org.organization_id && <Check size={16} style={{ color: aispaceColor }} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full border rounded-lg px-4 py-3 text-[14px] bg-[#F9FAFB] text-[#6B7280] border-[#E5E7EB] cursor-not-allowed">
                {callerHasOrg
                  ? (organizations.find((o) => o.organization_id === callerOrgId)?.organization_name || callerOrgId)
                  : t('users.addUserNoOrgError')}
              </div>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-[14px] font-medium text-[#111827] mb-1.5">
              {t('users.inviteModalRoleLabel')}
            </label>
            <div className="relative" ref={roleRef}>
              <button
                type="button"
                onClick={() => setRoleOpen((o) => !o)}
                className="w-full flex items-center justify-between border rounded-lg px-4 py-3 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors outline-none"
                style={roleOpen ? { borderColor: aispaceColor } : { borderColor: '#E5E7EB' }}
              >
                <span className={role ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                  {role ? roleLabels[role] : t('users.inviteModalRolePlaceholder')}
                </span>
                <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${roleOpen ? 'rotate-180' : ''}`} />
              </button>
              {roleOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 py-1">
                  {roleOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => { setRole(opt); setRoleOpen(false); }}
                      className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${role === opt ? 'bg-[#F3F4F6]' : ''}`}
                    >
                      <span>{roleLabels[opt]}</span>
                      {role === opt && <Check size={16} style={{ color: aispaceColor }} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSend}
            disabled={loading || !!emailError}
            className="flex items-center justify-center gap-2 px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]"
            style={{ backgroundColor: aispaceColor }}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>{t('users.inviteModalSending')}</span>
              </>
            ) : (
              <span>{t('users.inviteModalSendButton')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const Users = () => {
  const { t, language } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { aispaceColor } = useThemeColor();
  const users = useSelector((state) => state.all.users);
  const status = useSelector((state) => state.all.status);
  const error = useSelector((state) => state.all.error);
  const organizations = useSelector((state) => state.organizations.organizations);
  const organizationsError = useSelector((state) => state.organizations.error);

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [organizationModalOpen, setOrganizationModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalAction, setStatusModalAction] = useState('deactivate'); // 'deactivate' | 'activate'
  const [statusModalLoading, setStatusModalLoading] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openGroupsRow, setOpenGroupsRow] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);
  // Fallback (7 days in minutes) matches the previous hardcoded behavior in
  // case this hasn't loaded yet — kept in sync with the backend's own
  // INVITE_TOKEN_EXPIRE_MINUTES via the fetch below instead of a second,
  // independently-hardcoded duration.
  const [inviteExpiryMinutes, setInviteExpiryMinutes] = useState(7 * 24 * 60);
  const itemsPerPage = 6;

  // Filter modal state
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterUserGroups, setFilterUserGroups] = useState([]);
  const [filterOrgs, setFilterOrgs] = useState([]);
  const [filterRoles, setFilterRoles] = useState([]);
  const [filterSortBy, setFilterSortBy] = useState('newest');

  // ADMIN 0: orgId → string[] of department names (for displaying auto-assigned departments)
  const [orgDepartmentsMap, setOrgDepartmentsMap] = useState({});
  // All department names across every org (for SUPER_ADMIN rows)
  const [allDeptNames, setAllDeptNames] = useState([]);

  const { data, loading } = useSelector((state) => state.admin);
  const isSuperAdmin = data?.user_role === ROLE.SUPER_ADMIN;
  const isAdmin = data?.user_role === ROLE.ADMIN;
  const { addToast, ToastContainer } = useToast();

  useEffect(() => {
    axios.get(`${BASE_AUTH_URL}/invite-expiry-minutes`)
      .then((res) => {
        if (res.data?.minutes) setInviteExpiryMinutes(res.data.minutes);
      })
      .catch(() => {}); // keep the 7-day fallback if this fails
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading && data) {
        if (!isSuperAdmin && !isAdmin) {
          if (data?.user_role === ROLE.NO_LICENSE) {
            navigate('/hilfe');
            return;
          }
          navigate('/');
          return;
        }
        dispatch(fetchAllUsers());
        dispatch(fetchAllOrganizations());
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [dispatch, navigate, loading, isSuperAdmin, isAdmin, data]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
      setOpenGroupsRow(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ADMIN 0: fetch all departments for every org so ADMIN/SUPER_ADMIN rows can
  // display their auto-assigned departments in the Benutzergruppe column.
  useEffect(() => {
    if (!organizations || organizations.length === 0) return;
    let cancelled = false;
    Promise.all(
      organizations.map((org) =>
        axios
          .get(`${BASE_URL}/departments/${org.organization_id}`, authHeaders())
          .then((res) => ({ orgId: org.organization_id, depts: res.data?.departments || [] }))
          .catch(() => ({ orgId: org.organization_id, depts: [] })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      const seen = new Set();
      const allNames = [];
      results.forEach(({ orgId, depts }) => {
        map[orgId] = depts.map((d) => d.department_name).filter(Boolean);
        depts.forEach((d) => {
          if (d.department_name && !seen.has(d.department_name)) {
            seen.add(d.department_name);
            allNames.push(d.department_name);
          }
        });
      });
      setOrgDepartmentsMap(map);
      setAllDeptNames(allNames);
    });
    return () => { cancelled = true; };
  }, [organizations]);

  // Only the very first load (no users fetched yet) blanks the page with a
  // spinner. Every later fetchAllUsers() call — after resend, activate/
  // deactivate, role change, etc. — sets this same `status` to 'loading' too,
  // but by then `users` is already populated, so the existing table just
  // keeps showing (with the specific row's own inline spinner covering that
  // action) instead of the whole page unmounting and flashing a spinner.
  const isInitialUsersLoad = status === 'loading' && (!users || users.length === 0);

  if (loading || !data || isInitialUsersLoad) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: aispaceColor }}></div>
      </div>
    );
  }

  const handleRoleChange = async (newRole) => {
    if (!selectedUser) return;
    try {
      await dispatch(updateUserRole({
        userEmail: selectedUser.user_email,
        newRole
      })).unwrap();
      setRoleModalOpen(false);
      setSelectedUser(null);
      addToast(t('users.toastRoleUpdated'), "success");
    } catch (err) {
      console.error('Failed to update role:', err);
      addToast(t('users.toastRoleUpdateError'), "error");
    }
  };

  const handleOrganizationChange = async (organizationId, departmentIds = []) => {
    if (!selectedUser) return;
    try {
      await dispatch(updateUserOrganization({
        user_email: selectedUser.user_email,
        organization_id: organizationId,
        department_ids: departmentIds,
      })).unwrap();
      const modifiedEmail = selectedUser.user_email;
      setOrganizationModalOpen(false);
      setSelectedUser(null);
      dispatch(fetchAllUsers());
      // If the admin modified their own assignments, refresh the logged-in
      // user data so the HomeScreen tabs/agents update without a page reload.
      if (data?.user_email && modifiedEmail === data.user_email) {
        dispatch(fetchUserData(modifiedEmail));
      }
      addToast(t('users.toastOrgAssigned'), "success");
    } catch (err) {
      console.error('Failed to update organization:', err);
      addToast(t('users.toastOrgAssignError'), "error");
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setRoleModalOpen(true);
    setOpenDropdown(null);
  };

  const openActivityModal = (user) => {
    setSelectedUser(user);
    setActivityModalOpen(true);
    setOpenDropdown(null);
  };

  const openOrganizationModal = (user) => {
    setSelectedUser(user);
    setOrganizationModalOpen(true);
    setOpenDropdown(null);
  };

  const openStatusModal = (user, action) => {
    setSelectedUser(user);
    setStatusModalAction(action);
    setStatusModalOpen(true);
    setOpenDropdown(null);
  };

  // Re-runs the same /invite-user call the initial invite used — on an
  // existing, still-pending (no password/fullname) row this re-sends the
  // email with a freshly-issued token/expiry instead of erroring, per
  // routers/auth.py's own re-invite handling.
  const handleResendInvite = async (user) => {
    setOpenDropdown(null);
    setResendingInvite(user.entra_id);
    try {
      await axios.post(
        `${BASE_AUTH_URL}/invite-user`,
        { email: user.user_email, role: user.user_role, language, organization_id: user.organization_id || null },
        authHeaders(),
      );
      addToast(t('users.resendInviteSuccessToast'), 'success');
      dispatch(fetchAllUsers());
    } catch (err) {
      addToast(getErrorMessage(err, t('users.resendInviteErrorToast')), 'error');
    } finally {
      setResendingInvite(null);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedUser) return;
    setStatusModalLoading(true);
    try {
      if (statusModalAction === 'resetMfa') {
        await axios.post(`${BASE_AUTH_URL}/mfa/reset`, { email: selectedUser.user_email }, authHeaders());
        addToast(t('users.toastMfaReset'), 'success');
      } else if (statusModalAction === 'deactivate') {
        await dispatch(deactivateUser(selectedUser.user_email)).unwrap();
        addToast(t('users.toastUserDeactivated'), 'success');
      } else {
        await dispatch(activateUser(selectedUser.user_email)).unwrap();
        addToast(t('users.toastUserActivated'), 'success');
      }
      setStatusModalOpen(false);
      setSelectedUser(null);
    } catch (err) {
      addToast(
        typeof err === 'string' ? err : getErrorMessage(err, t('users.toastStatusChangeError')),
        'error',
      );
    } finally {
      setStatusModalLoading(false);
    }
  };

  // ITEM 9: ADMIN sees only users from their own organization
  const adminOrgId = data?.organization_id;

  // For ADMIN: scope user list to their org. If their own org_id is not set yet
  // (e.g. invited before org-assignment was in place), show all non-SUPER_ADMIN users.
  const scopedUsers = isSuperAdmin
    ? users
    : users.filter((u) => {
        if (u.user_role === ROLE.SUPER_ADMIN) return false;
        if (!adminOrgId) return true; // org not assigned yet — show all
        const userOrgId = u.organization_id || u.organisation_id;
        return userOrgId === adminOrgId;
      });

  // Departments — derived from org-scoped users only
  const userGroupOptions = (() => {
    const seen = new Set();
    const out = [];
    scopedUsers.forEach((u) => {
      const names = Array.isArray(u.department_names) && u.department_names.length > 0
        ? u.department_names
        : [u.department_name || u.user_group || u.group_name].filter(Boolean);
      names.forEach((name) => {
        if (name && !seen.has(name)) {
          seen.add(name);
          out.push({ code: name, label: name });
        }
      });
    });
    return out;
  })();

  // Organizations — ADMIN sees only their own org
  const organizationOptions = isSuperAdmin
    ? (organizations || []).map((o) => ({ code: o.organization_id, label: o.organization_name }))
    : (organizations || [])
        .filter((o) => o.organization_id === adminOrgId)
        .map((o) => ({ code: o.organization_id, label: o.organization_name }));

  // Filter users by search + filter modal selections.
  const filteredUsers = users
    .filter((user) => {
      // Scope ADMIN to their org only — hide other orgs and SUPER_ADMINs
      if (!isSuperAdmin) {
        if (user.user_role === ROLE.SUPER_ADMIN) return false;
        if (adminOrgId) {
          const userOrgId = user.organization_id || user.organisation_id;
          if (userOrgId !== adminOrgId) return false;
        }
        // adminOrgId null → show all non-SUPER_ADMIN users (org not assigned yet)
      }

      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        user.user_fullname?.toLowerCase().includes(q) ||
        user.user_email?.toLowerCase().includes(q) ||
        user.organization_name?.toLowerCase().includes(q);
      if (!matchesSearch) return false;

      if (filterRoles.length > 0 && !filterRoles.includes(user.user_role || ROLE.NO_LICENSE)) {
        return false;
      }
      if (filterOrgs.length > 0) {
        const orgId = user.organization_id || user.organisation_id;
        if (!filterOrgs.includes(orgId)) return false;
      }
      if (filterUserGroups.length > 0) {
        const userGroupNames = Array.isArray(user.department_names) && user.department_names.length > 0
          ? user.department_names
          : [user.department_name || user.user_group || user.group_name].filter(Boolean);
        const matches = userGroupNames.some((n) => filterUserGroups.includes(n));
        if (!matches) return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (filterSortBy) {
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name_asc':
          return (a.user_fullname || '').localeCompare(b.user_fullname || '');
        case 'name_desc':
          return (b.user_fullname || '').localeCompare(a.user_fullname || '');
        case 'newest':
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

  const activeFilterCount =
    (filterRoles.length > 0 ? 1 : 0) +
    (filterOrgs.length > 0 ? 1 : 0) +
    (filterUserGroups.length > 0 ? 1 : 0) +
    (filterSortBy !== 'newest' ? 1 : 0);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case ROLE.SUPER_ADMIN:
        return 'bg-[#FEE2E2] text-[#DC2626]';
      case ROLE.ADMIN:
        return 'bg-[#EDE9FE] text-[#7C3AED]';
      case ROLE.NORMAL:
        return 'bg-[#DBEAFE] text-[#2563EB]';
      default:
        return 'bg-[#F3F4F6] text-[#6B7280]';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case ROLE.SUPER_ADMIN:
        return t('users.roleSuperAdmin');
      case ROLE.ADMIN:
        return t('users.roleAdmin');
      case ROLE.NORMAL:
        return t('users.roleNormal');
      default:
        return t('users.roleNoLicense');
    }
  };

  if (status === 'failed') {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500">{t('users.errorPrefix', { error })}</p>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />

      {organizationsError && (
        <div className="mx-6 mt-4 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{organizationsError}</div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        {/* Search and Filters */}
        <div className="flex items-center gap-4 px-8 py-4">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder={t('users.pageSearchPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none transition-colors"
              onFocus={(e) => (e.target.style.borderColor = aispaceColor)}
              onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
            />
          </div>
          <button
            onClick={() => setFilterOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            <FilterIcon size={16} />
            {t('users.pageFilterButton')}
            {activeFilterCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold text-white rounded-full"
                style={{ backgroundColor: aispaceColor }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
          {(isSuperAdmin || isAdmin) && (
            <div className="flex items-center gap-3 ml-auto">
              <button
                onClick={() => setInviteModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors whitespace-nowrap"
                style={{ backgroundColor: aispaceColor }}
              >
                {t('users.inviteUserMenuLabel')}
              </button>
              <button
                onClick={() => setAddUserModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
              >
                {t('users.addUserMenuLabel')}
              </button>
            </div>
          )}
        </div>

        {/* Table Header */}
        <div className="flex items-center px-8 py-3 border-t border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="flex-1 min-w-0 pr-4 text-[13px] font-medium text-[#6B7280]">{t('users.columnFullName')}</div>
          <div className="flex-1 min-w-0 pr-4 text-[13px] font-medium text-[#6B7280]">{t('users.columnEmail')}</div>
          <div className="flex-1 min-w-0 pr-4 text-[13px] font-medium text-[#6B7280]">{t('users.columnOrganization')}</div>
          <div className="flex-1 min-w-0 pr-4 text-[13px] font-medium text-[#6B7280]">{t('users.columnUserGroup')}</div>
          <div className="flex-1 min-w-0 pr-4 text-[13px] font-medium text-[#6B7280]">{t('users.columnCreatedAt')}</div>
          <div className="flex-1 min-w-0 pr-4 text-[13px] font-medium text-[#6B7280]">{t('users.columnRole')}</div>
          <div className="w-[50px] flex-shrink-0"></div>
        </div>

        {/* Table Body */}
        <div>
          {paginatedUsers.map((user, index) => {
            const isLastItem = index >= paginatedUsers.length - 2;
            const isDropdownOpen = openDropdown === user.entra_id;
            // Lifted out of the badge's own IIFE below so the row-actions
            // dropdown can also gate its "Resend Invitation" item on the
            // same isExpiredInvite value, instead of recomputing it twice.
            const isPendingInvite = !user.user_fullname;
            const invitedAt = user.created_at ? new Date(user.created_at) : null;
            const isExpiredInvite = isPendingInvite && invitedAt && (Date.now() - invitedAt.getTime() > inviteExpiryMinutes * 60 * 1000);

            return (
              <div
                key={user.entra_id}
                className={`flex items-center px-8 py-5 hover:bg-[#FAFAFA] transition-colors ${
                  index !== paginatedUsers.length - 1 ? 'border-b border-[#E5E7EB]' : ''
                }`}
              >
                {/* Name */}
                <div className="flex-1 min-w-0 pr-4 flex items-center gap-2">
                  {user.user_fullname ? (
                    <>
                      <span className="text-[14px] font-medium text-[#111827] truncate">{user.user_fullname}</span>
                      {user.is_active === false && (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[#FEE2E2] text-[#991B1B] flex-shrink-0">
                          {t('users.statusInactive')}
                        </span>
                      )}
                    </>
                  ) : isExpiredInvite ? (
                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[#FEE2E2] text-[#991B1B] flex-shrink-0">
                      {t('users.statusExpired')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[#FEF3C7] text-[#92400E] flex-shrink-0">
                      {t('users.statusPending')}
                    </span>
                  )}
                </div>

                {/* Email */}
                <div className="flex-1 min-w-0 pr-4 text-[14px] text-[#6B7280] truncate">
                  {user.user_email}
                </div>

                {/* Organization */}
                <div className="flex-1 min-w-0 pr-4 text-[14px] text-[#111827] truncate">
                  {user.organization_name || '-'}
                </div>

                {/* Benutzergruppe (User Groups) — single line; "+N mehr" reveals full list */}
                {(() => {
                  // ADMIN 0: ADMIN/SUPER_ADMIN are auto-part of all departments — show them all.
                  const groups = (() => {
                    if (user.user_role === ROLE.SUPER_ADMIN) return allDeptNames;
                    if (user.user_role === ROLE.ADMIN) {
                      const orgId = user.organization_id || user.organisation_id;
                      return orgDepartmentsMap[orgId] || [];
                    }
                    return Array.isArray(user.department_names) && user.department_names.length > 0
                      ? user.department_names
                      : [user.department_name || user.user_group || user.group_name].filter(Boolean);
                  })();
                  const isOpen = openGroupsRow === user.entra_id;
                  const remaining = Math.max(0, groups.length - 1);
                  return (
                    <div className="flex-1 min-w-0 pr-4 flex items-center gap-2 relative">
                      {groups.length === 0 ? (
                        <span className="text-[14px] text-[#111827]">-</span>
                      ) : (
                        <>
                          <span className="text-[14px] text-[#111827] truncate" title={groups[0]}>
                            {groups[0]}
                          </span>
                          {remaining > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenGroupsRow(isOpen ? null : user.entra_id);
                              }}
                              className="flex-shrink-0 inline-flex items-center px-2 py-0.5 text-[12px] font-medium rounded-full border border-[#E5E7EB] text-[#374151] hover:bg-[#F3F4F6] transition-colors"
                            >
                              {t('users.userGroupMoreLabel', { count: remaining })}
                            </button>
                          )}
                          {isOpen && (
                            <div
                              className="absolute top-full left-0 mt-2 w-[220px] bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-[60] py-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {groups.map((g) => (
                                <div
                                  key={g}
                                  className="px-3 py-1.5 text-[13px] text-[#111827] hover:bg-[#F9FAFB]"
                                >
                                  {g}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Created Date */}
                <div className="flex-1 min-w-0 pr-4 text-[14px] text-[#6B7280] truncate">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString('de-DE') : '-'}
                </div>

                {/* Role */}
                <div className="flex-1 min-w-0 pr-4">
                  <span className={`inline-block px-3 py-1 text-[12px] font-medium rounded-full ${getRoleBadgeStyle(user.user_role)}`}>
                    {getRoleLabel(user.user_role)}
                  </span>
                </div>

                {/* Actions */}
                <div className="w-[50px] flex-shrink-0 flex items-center justify-end relative">
                  {(isSuperAdmin || user.user_role !== ROLE.SUPER_ADMIN) ? (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (resendingInvite === user.entra_id) return;
                          setOpenDropdown(isDropdownOpen ? null : user.entra_id);
                        }}
                        disabled={resendingInvite === user.entra_id}
                        className="p-1.5 text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {resendingInvite === user.entra_id ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <MoreHorizontal size={18} />
                        )}
                      </button>

                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className={`absolute right-0 ${isLastItem ? 'bottom-full mb-2' : 'top-full mt-2'} min-w-[280px] bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-[999] py-2`}>
                          <button
                            onClick={() => openRoleModal(user)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                          >
                            <UserRoundPlus size={16} className="text-[#374151] flex-shrink-0" />
                            {t('users.actionAssignRole')}
                          </button>
                          {/* Role hierarchy: ADMIN and SUPER_ADMIN can deactivate anyone in their
                              org (including another ADMIN) except a SUPER_ADMIN or themselves. */}
                          {user.user_role !== ROLE.SUPER_ADMIN && user.user_email !== data?.user_email && (isSuperAdmin || isAdmin) && (
                            user.is_active === false ? (
                              <button
                                onClick={() => openStatusModal(user, 'activate')}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                              >
                                <Power size={16} className="text-[#374151] flex-shrink-0" />
                                {t('users.actionActivateUser')}
                              </button>
                            ) : (
                              <button
                                onClick={() => openStatusModal(user, 'deactivate')}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                              >
                                <PowerOff size={16} className="text-[#374151] flex-shrink-0" />
                                {t('users.actionDeactivateUser')}
                              </button>
                            )
                          )}
                          {user.user_role !== ROLE.SUPER_ADMIN && user.user_email !== data?.user_email && (isSuperAdmin || isAdmin) && (
                            <button
                              onClick={() => openStatusModal(user, 'resetMfa')}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                            >
                              <KeyRound size={16} className="text-[#374151] flex-shrink-0" />
                              {t('users.actionResetMfa')}
                            </button>
                          )}
                          {/* ITEM 9: ADMIN cannot change user's organization */}
                          {isSuperAdmin && (
                            <button
                              onClick={() => openOrganizationModal(user)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                            >
                              <Building2 size={16} className="text-[#374151] flex-shrink-0" />
                              {t('users.actionAssignOrganization')}
                            </button>
                          )}
                          {/* ADMIN can assign departments within their org */}
                          {isAdmin && !isSuperAdmin && (
                            <button
                              onClick={() => openOrganizationModal(user)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                            >
                              <Building2 size={16} className="text-[#374151] flex-shrink-0" />
                              {t('users.actionAssignDepartment')}
                            </button>
                          )}
                          <button
                            onClick={() => openActivityModal(user)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                          >
                            <Activity size={16} className="text-[#374151] flex-shrink-0" />
                            {t('users.actionCheckActivity')}
                          </button>
                          {/* No loading/disabled state needed here — the
                              dropdown closes as soon as this fires (see
                              handleResendInvite) and the 3-dot button above
                              takes over as the spinner for the rest of the
                              request, so this button is never visible while
                              a resend for this row is in flight. */}
                          {isExpiredInvite && (
                            <button
                              onClick={() => handleResendInvite(user)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                            >
                              <Send size={16} className="text-[#374151] flex-shrink-0" />
                              {t('users.actionResendInvite')}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-1.5 opacity-0">
                      <MoreHorizontal size={18} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-8 py-4 border-t border-[#E5E7EB]">
          <span className="text-[14px] text-[#6B7280]">
            {t('pagination.pageOf', { current: currentPage, total: totalPages || 1 })}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.back')}
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <RoleModal
        isOpen={roleModalOpen}
        onClose={() => {
          setRoleModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleRoleChange}
        currentRole={selectedUser?.user_role || ROLE.NORMAL}
        userName={selectedUser?.user_fullname}
        aispaceColor={aispaceColor}
        callerIsSuperAdmin={isSuperAdmin}
      />

      <OrganizationModal
        isOpen={organizationModalOpen}
        onClose={() => {
          setOrganizationModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleOrganizationChange}
        organizations={isSuperAdmin ? organizations : organizations.filter(org =>
          org.organization_id === adminOrgId
        )}
        currentOrganization={selectedUser?.organization_id}
        currentDepartmentIds={selectedUser?.department_ids || []}
        aispaceColor={aispaceColor}
        isAdminCaller={
          !isSuperAdmin && (selectedUser?.user_role === ROLE.ADMIN || selectedUser?.user_role === ROLE.SUPER_ADMIN)
        }
        hideOrgPicker={!isSuperAdmin}
      />

      <ActivityModal
        isOpen={activityModalOpen}
        onClose={() => {
          setActivityModalOpen(false);
          setSelectedUser(null);
        }}
        userEmail={selectedUser?.user_email}
        userName={selectedUser?.user_fullname}
        aispaceColor={aispaceColor}
      />

      <StatusConfirmModal
        isOpen={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleConfirmStatusChange}
        action={statusModalAction}
        userName={selectedUser?.user_fullname}
        userEmail={selectedUser?.user_email}
        loading={statusModalLoading}
        aispaceColor={aispaceColor}
      />

      <FilterModal
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={({ userGroups, organizations: orgs, roles, sortBy }) => {
          setFilterUserGroups(userGroups);
          setFilterOrgs(orgs);
          setFilterRoles(roles);
          setFilterSortBy(sortBy);
          setCurrentPage(1);
        }}
        onClearAll={() => {
          setFilterUserGroups([]);
          setFilterOrgs([]);
          setFilterRoles([]);
          setFilterSortBy('newest');
          setCurrentPage(1);
        }}
        initialUserGroups={filterUserGroups}
        initialOrganizations={filterOrgs}
        initialRoles={filterRoles}
        initialSortBy={filterSortBy}
        userGroupOptions={
          (isSuperAdmin
            ? allDeptNames
            : (orgDepartmentsMap[adminOrgId] || [])
          ).map((name) => ({ code: name, label: name }))
        }
        organizationOptions={organizationOptions}
        aispaceColor={aispaceColor}
        callerIsSuperAdmin={isSuperAdmin}
      />
      <AddUserModal
        isOpen={addUserModalOpen}
        onClose={() => setAddUserModalOpen(false)}
        aispaceColor={aispaceColor}
        callerIsSuperAdmin={isSuperAdmin}
        callerHasOrg={!!adminOrgId}
        organizations={organizations}
        callerOrgId={adminOrgId}
        onSuccess={() => {
          setAddUserModalOpen(false);
          addToast(t('users.addUserSuccessToast'), 'success');
          dispatch(fetchAllUsers());
        }}
      />
      <InviteUserModal
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        aispaceColor={aispaceColor}
        callerIsSuperAdmin={isSuperAdmin}
        callerHasOrg={!!adminOrgId}
        organizations={organizations}
        callerOrgId={adminOrgId}
        existingEmails={users.map((u) => (u.user_email || '').toLowerCase())}
        onSuccess={() => {
          addToast(t('users.inviteModalSuccessToast'), 'success');
          dispatch(fetchAllUsers());
        }}
      />
    </div>
  );
};

export default Users;
